import { quoteLineType } from '../../types'
import { BUNDLE_NON_EDIT_PROPERTIES } from './Lines'
import { productTypeForCategory } from './productCategories'

const PRICING_PROPERTIES: (keyof quoteLineType)[] = [
  'UnitCost',
  'UnitPrice',
  'Qty',
  'Margin',
  'TotalFees',
  'CreditCardFee',
  'OEMCode',
  'MfrPartNumber',
]

export type BundleLineItem = quoteLineType & { children?: quoteLineType[] }

const calculateGPPercent = (unitPrice: number, unitCost: number, totalFees: number, quantity: number): number => {
  return (quantity * unitPrice - quantity * unitCost - totalFees) / (quantity * unitPrice)
}

const billableLines = (lineItems: BundleLineItem[]): quoteLineType[] =>
  lineItems.flatMap(item => {
    if (item.LineNumber === -1) {
      return (item.children ?? []).filter(child => child.LineNumber !== -1)
    }
    return [item]
  })

const applyPricingRecalc = (
  item: quoteLineType,
  property: keyof quoteLineType,
  value: quoteLineType[keyof quoteLineType],
): quoteLineType => {
  const updated = { ...item, [property]: value }
  const unitCost = updated.UnitCost ?? 0
  const unitPrice = updated.UnitPrice ?? 0
  const qty = updated.Qty ?? 0
  const totalFees = updated.TotalFees ?? 0
  const costWithFees = unitCost + totalFees

  switch (property) {
    case 'UnitCost':
      return { ...updated, ExtCost: unitCost * qty, Margin: calculateGPPercent(unitPrice, unitCost, totalFees, qty) }
    case 'UnitPrice':
      return {
        ...updated,
        ExtPrice: unitPrice * qty,
        Margin: calculateGPPercent(unitPrice, unitCost, totalFees, qty),
      }
    case 'Qty':
      return {
        ...updated,
        ExtPrice: unitPrice * qty,
        ExtCost: unitCost * qty,
      }
    case 'Margin': {
      const margin = (value as number | null) ?? 0
      const newUnitPrice = costWithFees / (1 - margin) / qty
      return {
        ...updated,
        UnitPrice: newUnitPrice,
        ExtPrice: newUnitPrice * qty,
        Margin: margin,
      }
    }
    case 'TotalFees':
      return {
        ...updated,
        Margin: calculateGPPercent(unitPrice, unitCost, totalFees, qty),
      }
    case 'OEMCode':
      return {
        ...updated,
        CLIN: `${value ?? ''}${updated.MfrPartNumber ?? ''}`,
      }
    case 'MfrPartNumber':
      return {
        ...updated,
        CLIN: `${updated.OEMCode ?? ''}${value ?? ''}`,
      }
    default:
      return updated
  }
}

const recalculateBundleHeaderTotals = (
  bundle: quoteLineType & { children: quoteLineType[] },
): quoteLineType & { children: quoteLineType[] } => {
  const newBundle = {
    ...bundle,
    UnitCost: bundle.children.reduce((acc, child) => acc + (child.UnitCost ?? 0) * (child.Qty ?? 0), 0),
    ExtCost: bundle.children.reduce((acc, child) => acc + (child.ExtCost ?? 0), 0),
    UnitPrice: bundle.children.reduce((acc, child) => acc + (child.UnitPrice ?? 0) * (child.Qty ?? 0), 0),
    ExtPrice: bundle.children.reduce((acc, child) => acc + (child.ExtPrice ?? 0), 0),
    TotalFees: bundle.children.reduce((acc, child) => acc + (child.TotalFees ?? 0), 0),
  }

  newBundle.Margin = calculateGPPercent(
    newBundle.UnitPrice,
    newBundle.UnitCost,
    newBundle.TotalFees,
    newBundle.Qty ?? 0,
  )
  return newBundle
}

const floorCents = (n: number): number => Math.floor(n * 100 + 1e-9) / 100

const applyCostPlusPercent = (line: quoteLineType, percent: number): quoteLineType => {
  const unitCost = line.UnitCost ?? 0
  const qty = line.Qty ?? 0
  const unitPrice = floorCents(unitCost * (1 + percent))
  return {
    ...line,
    UnitPrice: unitPrice,
    ExtPrice: floorCents(unitPrice * qty),
    Margin: calculateGPPercent(unitPrice, unitCost, line.TotalFees ?? 0, qty),
  }
}

export const applyNTECalculation = (lineItems: BundleLineItem[], nteAmount: number): BundleLineItem[] => {
  const lines = billableLines(lineItems)
  const totalCost = lines.reduce((sum, line) => sum + (line.ExtCost ?? 0), 0)
  const totalCents = (percent: number) =>
    Math.round(lines.reduce((sum, line) => sum + (applyCostPlusPercent(line, percent).ExtPrice ?? 0), 0) * 100)
  const nteCents = Math.round(nteAmount * 100)
  let basisPoints = totalCost <= 0 ? 0 : Math.floor((nteAmount / totalCost - 1) * 10000)
  while (basisPoints > -10000 && totalCents(basisPoints / 10000) > nteCents) {
    basisPoints--
  }
  const percent = basisPoints / 10000

  return lineItems.map(item => {
    if (item.LineNumber === -1) {
      const children = item.children?.map(child => applyCostPlusPercent(child, percent)) ?? []
      return { ...item, children }
    }
    return { ...item, ...applyCostPlusPercent(item, percent) }
  })
}

const samePartValue = (a: unknown, b: unknown): boolean =>
  String(a ?? '')
    .trim()
    .toUpperCase() ===
  String(b ?? '')
    .trim()
    .toUpperCase()

// Labor Rate lines stay Labor Rate when categorized as a service
const productTypeForPick = (item: quoteLineType, categoryName: string | null): string | null => {
  const derived = productTypeForCategory(categoryName)
  if (!derived) return item.ProductType
  if (item.ProductType === 'Labor Rate' && derived === 'Service') return item.ProductType
  return derived
}

// Picks are MANUAL; a new part clears an automatic category so the server re-resolves it. Bundle headers skip this.
const applyCategoryChange = (
  item: quoteLineType,
  property: keyof quoteLineType,
  value: quoteLineType[keyof quoteLineType],
): quoteLineType => {
  if (item.LineNumber === -1) return item
  if (property === 'ProductCategory') {
    const name = value === null || value === undefined || value === '' ? null : String(value)
    return {
      ...item,
      ProductCategory: name,
      ProductCategorySource: name === null ? null : 'MANUAL',
      ProductCategoryConfidence: name === null ? null : 1,
      ProductCategoryAttempts: 0,
      ProductCategoryAttemptAt: null,
      ProductType: productTypeForPick(item, name),
    }
  }
  if (
    (property === 'MfrPartNumber' || property === 'Manufacturer') &&
    item.ProductCategorySource !== 'MANUAL' &&
    !samePartValue(item[property], value)
  ) {
    return {
      ...item,
      ProductCategory: null,
      ProductCategorySource: null,
      ProductCategoryConfidence: null,
      ProductCategoryKey: null,
      ProductCategoryAttempts: 0,
      ProductCategoryAttemptAt: null,
    }
  }
  return item
}

// Part changes first, so a category picked in the same edit is kept
export const applyLineEdits = (item: quoteLineType, patch: Partial<quoteLineType>): quoteLineType => {
  const { ProductCategory: pickedCategory, ...fields } = patch
  let updated: quoteLineType = item
  for (const property of ['MfrPartNumber', 'Manufacturer'] as const) {
    if (property in fields) updated = applyCategoryChange(updated, property, fields[property] ?? null)
  }
  updated = { ...updated, ...fields }
  if ('ProductCategory' in patch) {
    const picked = applyCategoryChange(updated, 'ProductCategory', pickedCategory ?? null)
    updated = 'ProductType' in fields ? { ...picked, ProductType: updated.ProductType } : picked
  }
  return updated
}

const applySingleLineItemProperty = (
  items: BundleLineItem[],
  lineItemId: string,
  property: keyof quoteLineType,
  value: quoteLineType[keyof quoteLineType],
): BundleLineItem[] => {
  const updateItem = (current: quoteLineType): quoteLineType => {
    const item = applyCategoryChange(current, property, value)
    if (property === 'ProductCategory') return item
    if (item.LineNumber === -1 && !BUNDLE_NON_EDIT_PROPERTIES.includes(property)) {
      return applyPricingRecalc(item, property, value)
    }
    if (!PRICING_PROPERTIES.includes(property)) {
      return { ...item, [property]: value }
    }
    return applyPricingRecalc(item, property, value)
  }

  return items.map(item => {
    if (item.LineItemId === lineItemId) {
      return { ...updateItem(item), children: item.children }
    }
    if (item.LineNumber === -1 && item.children?.some(child => child.LineItemId === lineItemId)) {
      const updatedChildren = item.children.map(child => (child.LineItemId === lineItemId ? updateItem(child) : child))
      const updatedBundle = { ...item, children: updatedChildren }
      if (PRICING_PROPERTIES.includes(property)) {
        return recalculateBundleHeaderTotals(updatedBundle)
      }
      return updatedBundle
    }
    return item
  })
}

export function updateLineItemProperty(
  items: BundleLineItem[],
  lineItemId: string,
  property: keyof quoteLineType,
  value: quoteLineType[keyof quoteLineType],
): BundleLineItem[]
export function updateLineItemProperty(
  items: BundleLineItem[],
  lineItemId: string,
  patch: Partial<quoteLineType>,
): BundleLineItem[]
export function updateLineItemProperty(
  items: BundleLineItem[],
  lineItemId: string,
  propertyOrPatch: keyof quoteLineType | Partial<quoteLineType>,
  value?: quoteLineType[keyof quoteLineType],
): BundleLineItem[] {
  const patch: Partial<quoteLineType> =
    typeof propertyOrPatch === 'object' ? propertyOrPatch : { [propertyOrPatch]: value }

  return (Object.entries(patch) as [keyof quoteLineType, quoteLineType[keyof quoteLineType]][]).reduce(
    (acc, [property, propertyValue]) => applySingleLineItemProperty(acc, lineItemId, property, propertyValue),
    items,
  )
}
